import {
  Box,
  Button,
  Modal,
  SearchableList,
  SearchInput,
  classSet
} from '@platform/ui-library';
import { FC, useState } from 'react';
import { useIntentApplications } from '../../hooks';
import { CommonIntent } from '../../types';
import { convertToSentence } from '../../utilities/functions/string/convert-to-sentence';
import { Table } from '../table';
import styles from './ImportFromLibrary.module.scss';
import intentStyles from './IntentDetails.module.scss';

interface ImportFromLibraryProps {
  canSave?: boolean;
  commonIntents: CommonIntent[];
  isOpen?: boolean;
  onCancel: () => void;
  onImport: (commonIntent: CommonIntent) => void;
}

export const ImportFromLibrary: FC<ImportFromLibraryProps> = (props) => {
  const [commonIntentTableSearch, setCommonIntentTableSearch] = useState<string>('');
  const [selectedCommonIntent, setSelectedCommonIntent] = useState<CommonIntent>();
  const intentName = selectedCommonIntent?.name ?? '';
  const applications = useIntentApplications(intentName);

  const allTableData = props.commonIntents.map((commonIntent) => {
    const { commonId, name, description, localeData } = commonIntent;

    return {
      id: commonId,
      name,
      description,
      utterances: localeData['en-US']?.phrases.length,
      '': {
        content: <Button key={commonId} type='button' theme='outlineHighlight' onClick={() => setSelectedCommonIntent(commonIntent)}>View</Button>,
        raw: ''
      }
    };
  });

  const filteredTableData = commonIntentTableSearch
    ? allTableData.filter((item) => {
      const searchLower = commonIntentTableSearch.toLowerCase();
      return (
        item.name.toLowerCase().includes(searchLower) ||
        (item.description ?? '').toLowerCase().includes(searchLower)
      );
    })
    : allTableData;

  return (
    <Modal className={intentStyles.newModal} isOpen={props.isOpen}>
      <Box className={classSet([intentStyles.box, intentStyles.scroll])} theme="noPadding">
        <div className={classSet([intentStyles.header, intentStyles.withSubtitle])}>
          <h2 className={intentStyles.title}>Import From Library</h2>
          <p className={intentStyles.subtitle}>You can import these intents to add functionality to your application with built-in utterances.</p>
        </div>
        <div className={classSet([intentStyles.content, intentStyles.flex])}>
          {selectedCommonIntent ? (
            <div className={styles.intentView}>
              <div>
                <p>Intent Name: <strong>{selectedCommonIntent.name}</strong></p>
                {!!applications?.length && (
                  <p>
                    Used by:
                    <strong>
                      {` ${applications.join(', ')}`}
                    </strong>
                  </p>
                )}
                {!!selectedCommonIntent?.description &&
                  <p>{convertToSentence(selectedCommonIntent.description)}</p>
                }
                <SearchableList
                  hasCount
                  label="Utterances"
                  disabled
                  items={selectedCommonIntent?.localeData['en-US']?.phrases.map((phrase, i) => ({ id: 'utt'+i, value: phrase } )) ?? []}
                />
              </div>
              <div>
                <p>Regular Expression</p>
                <p>{selectedCommonIntent.localeData['en-US']?.regularExpression}</p>
                <hr />
                <SearchableList
                  hasCount={false}
                  label="Intent Hierarchy (Parents)"
                  disabled
                  items={selectedCommonIntent.parents.map((intent) => ({
                    id: intent.commonId,
                    value: intent.name
                  }))}
                />
                <hr />
                <SearchableList
                  hasCount={false}
                  label="Intent Hierarchy (Children)"
                  disabled
                  items={selectedCommonIntent.children.map((intent) => ({
                    id: intent.commonId,
                    value: intent.name
                  }))}
                />
              </div>
            </div>
          ) : (
            <>
              <SearchInput
                name="searchIntents"
                placeholder="Search Intents"
                onChange={(inputValue) => {
                  setCommonIntentTableSearch(inputValue);
                }}
                extraClasses={intentStyles.input}
                value={commonIntentTableSearch}
              />
              {!filteredTableData.length ? null : (
                <Table
                  className={styles.table}
                  data={filteredTableData}
                  sortByColumns={['name', 'utterances']}
                />
              )}
            </>
          )}
        </div>
        <div className={intentStyles.footer}>
          {selectedCommonIntent && (
            <Button type="button" theme="outline" onClick={() => setSelectedCommonIntent(undefined)}>
              Back
            </Button>
          )}
          <div className={intentStyles.buttonGroup}>
            <Button type="button" theme="outline" onClick={props.onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              theme="default"
              disabled={!selectedCommonIntent}
              onClick={() => selectedCommonIntent && props.onImport(selectedCommonIntent)}
            >
              Import
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};
